// The scrubbable waveform strip on the STT playground results and confirm
// pages (LAN-311).
//
// Everything drawn here comes from two places already on the page: the peaks
// are decoded in the browser from the same recording the <audio> element
// plays, and the bands, flags and engine lanes come from the data attribute
// the server stamps on the container (Account::CallPlaygroundsHelper#waveform_payload).
// The strip therefore adds one download and no request of its own, and it can
// never disagree with the by-turn table below it — same seconds, same anchor.
//
// If anything is missing (no AudioContext, a recording that will not decode,
// a browser that refuses the fetch) the strip hides itself and the page is
// exactly as it was.

// ---------------------------------------------------------------------------
// Pure helpers. Tested by test/javascript/call_playground_waveform_test.js.
// ---------------------------------------------------------------------------

// One peak per bucket: the loudest sample in the bucket, as a magnitude. The
// mean would flatten speech into a grey slab, and every sample would be a
// million-point loop on every redraw, so the decode caches a coarse array once
// and each redraw buckets that down again to the current pixel width.
const downsamplePeaks = (samples, buckets) => {
  const count = Math.max(0, Math.floor(buckets));
  const out = new Float32Array(count);
  if (!samples || !samples.length || count === 0) return out;

  const per = samples.length / count;
  for (let i = 0; i < count; i += 1) {
    const from = Math.floor(i * per);
    const to = Math.min(samples.length, Math.max(from + 1, Math.floor((i + 1) * per)));
    let max = 0;
    for (let j = from; j < to; j += 1) {
      const value = samples[j] < 0 ? -samples[j] : samples[j];
      if (value > max) max = value;
    }
    out[i] = max;
  }
  return out;
};

const secondsToX = (seconds, duration, width) => {
  if (!(duration > 0) || !(width > 0)) return 0;
  return (seconds / duration) * width;
};

const xToSeconds = (x, duration, width) => {
  if (!(duration > 0) || !(width > 0)) return 0;
  const seconds = (x / width) * duration;
  if (seconds < 0) return 0;
  if (seconds > duration) return duration;
  return seconds;
};

// The turn the playhead is in. The tolerance is the same idea the alignment
// uses server-side: a turn's stamped start is the moment speech was detected,
// so a scrub that lands a fraction before it is still that turn.
const TURN_TOLERANCE_SECONDS = 0.3;
const turnAtTime = (turns, seconds, tolerance = TURN_TOLERANCE_SECONDS) =>
  (turns || []).find(turn => seconds >= turn.start - tolerance && seconds <= turn.end + tolerance) || null;

// Segments an engine finalised back to back are one block on a 12px lane:
// drawn separately they are a dotted line of 2px slivers that reads as
// dropouts the engine did not have. Silent segments never merge into spoken
// ones — the whole point of the amber is that it is separable.
const LANE_MERGE_GAP_SECONDS = 0.15;
const laneClusters = (segments, minGapSeconds = LANE_MERGE_GAP_SECONDS) => {
  const sorted = [...(segments || [])].sort((a, b) => a.start - b.start);
  const out = [];
  sorted.forEach(segment => {
    const last = out[out.length - 1];
    if (last && last.silent === Boolean(segment.silent) && segment.start - last.end <= minGapSeconds) {
      last.end = Math.max(last.end, segment.end);
      last.count += 1;
      return;
    }
    out.push({
      start: segment.start,
      end: segment.end,
      silent: Boolean(segment.silent),
      count: 1,
    });
  });
  return out;
};

// A key press that belongs to a control, not to the strip. Typing in the
// confirm table must not scrub the recording, and space on the strip's own
// play button is already a click — handling it here as well would toggle
// twice and leave the recording exactly where it was.
const shouldIgnoreKey = target => {
  if (!target) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(target.tagName);
};

const ARROW_STEP_SECONDS = 1;
// A jump lands exactly on a turn start, so "next turn" has to look past where
// it just landed or ] would never move twice.
const TURN_JUMP_EPSILON = 0.2;

// What a key means, with no side effects: either a new position, a play/pause
// toggle, or nothing at all.
const keyboardStep = (key, state) => {
  const { position = 0, duration = 0, turns = [] } = state || {};
  if (key === ' ' || key === 'Spacebar' || key === 'Space') return { toggle: true };
  if (key === 'ArrowLeft') return { position: Math.max(0, position - ARROW_STEP_SECONDS) };
  if (key === 'ArrowRight') return { position: Math.min(duration, position + ARROW_STEP_SECONDS) };
  if (key === ']') {
    const next = turns.find(turn => turn.start > position + TURN_JUMP_EPSILON);
    return next ? { position: next.start } : null;
  }
  if (key === '[') {
    const previous = [...turns].reverse().find(turn => turn.start < position - TURN_JUMP_EPSILON);
    return previous ? { position: previous.start } : null;
  }
  return null;
};

const formatClock = seconds => {
  const whole = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// The strip itself.
// ---------------------------------------------------------------------------

// Turbolinks 5 restores a cached body on a Back visit: the data attributes
// survive, the listeners do not. Same WeakSet trick as
// app/javascript/account/call_playground_benchmark.js — empty on every load,
// so a restored node counts as new and gets its listeners back.
const boundNodes = new WeakSet();
const bindOnce = node => {
  if (!node || boundNodes.has(node)) return false;
  boundNodes.add(node);
  return true;
};

// Decode resolution. Fine enough that an 8x zoom still has more than one peak
// per pixel, small enough to keep (80 KB) for the life of the page.
const PEAK_CACHE_BUCKETS = 20000;

const WAVE_TOP = 26; // the turn-number ribbon lives above this
const WAVE_HEIGHT = 100;
const LANE_GAP_TOP = 18;
const LANE_HEIGHT = 12;
// Each lane's gap holds the engine's name, so the name never covers a segment.
const LANE_GAP = 18;
const STAGE_PADDING_BOTTOM = 8;
const MAX_ZOOM = 8;

const stageHeight = laneCount =>
  WAVE_TOP + WAVE_HEIGHT + LANE_GAP_TOP + laneCount * (LANE_HEIGHT + LANE_GAP) + STAGE_PADDING_BOTTOM;

// The palette is declared on .stt-wave in app/assets/stylesheets/_custom.scss
// so the canvas and the surrounding chrome cannot drift apart.
const readPalette = element => {
  const style = window.getComputedStyle(element);
  const read = (name, fallback) => style.getPropertyValue(name).trim() || fallback;
  return {
    wave: read('--stt-wave-idle', '#B4BFC9'),
    wavePlayed: read('--stt-wave-played', 'rgb(18, 180, 148)'),
    turn: read('--stt-wave-turn', 'rgba(18, 180, 148, 0.09)'),
    turnActive: read('--stt-wave-turn-active', 'rgba(18, 180, 148, 0.22)'),
    turnInk: read('--stt-wave-turn-ink', '#0E7C66'),
    tutor: read('--stt-wave-tutor', '#E4ECF3'),
    flag: read('--stt-wave-flag', '#B42318'),
    lane: read('--stt-wave-lane', '#EEF2F6'),
    laneOn: read('--stt-wave-lane-on', '#4338CA'),
    laneSilent: read('--stt-wave-lane-silent', '#D97706'),
    playhead: read('--stt-wave-playhead', '#26313D'),
    muted: read('--stt-wave-muted', '#6C757D'),
  };
};

const readPayload = root => {
  try {
    const payload = JSON.parse(root.dataset.waveform || 'null');
    if (!payload || !Array.isArray(payload.turns) || !payload.turns.length) return null;
    if (!(Number(payload.duration_seconds) > 0)) return null;
    return payload;
  } catch (error) {
    return null;
  }
};

const decodePeaks = async src => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass || !src) return null;

  const response = await fetch(src, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`The recording could not be read (${response.status}).`);
  const bytes = await response.arrayBuffer();
  const context = new AudioContextClass();
  try {
    // Safari still only has the callback form, so it is promisified here
    // rather than assumed away.
    const buffer = await new Promise((resolve, reject) => {
      const decoded = context.decodeAudioData(bytes, resolve, reject);
      if (decoded && typeof decoded.then === 'function') decoded.then(resolve, reject);
    });
    return {
      peaks: downsamplePeaks(buffer.getChannelData(0), PEAK_CACHE_BUCKETS),
      duration: buffer.duration,
    };
  } finally {
    if (typeof context.close === 'function') context.close();
  }
};

const initialize = root => {
  if (!bindOnce(root)) return;

  const payload = readPayload(root);
  // This card's player, not the first one on the page. The results page and
  // the confirm page each render their own .stt-call-audio, and a strip that
  // reached past its own card drove someone else's recording.
  const card = root.closest('.stt-call-audio');
  const audio = card && card.querySelector('audio');
  const canvas = root.querySelector('[data-waveform-canvas]');
  const scroller = root.querySelector('[data-waveform-scroller]');
  const stage = root.querySelector('[data-waveform-stage]');
  const status = root.querySelector('[data-waveform-status]');
  if (!payload || !audio || !canvas || !scroller || !stage) return;

  const context = canvas.getContext && canvas.getContext('2d');
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!context || !AudioContextClass) return;

  const timeLabel = root.querySelector('[data-waveform-time]');
  const chip = root.querySelector('[data-waveform-chip]');
  const zoomLabel = root.querySelector('[data-waveform-zoom]');
  const playButton = root.querySelector('[data-waveform-play]');
  const cursor = root.querySelector('[data-waveform-cursor]');
  const tip = root.querySelector('[data-waveform-tip]');

  const turns = payload.turns;
  const lanes = payload.lanes || [];
  const clusteredLanes = lanes.map(lane => ({
    label: lane.label || lane.provider,
    blocks: laneClusters(lane.segments || []),
  }));
  const height = stageHeight(clusteredLanes.length);

  let duration = Number(payload.duration_seconds);
  let peaks = null;
  let columnPeaks = null;
  let zoom = 1;
  let position = 0;
  let dragging = false;
  let frame = null;

  const width = () => stage.clientWidth || scroller.clientWidth || 1;
  const x = seconds => secondsToX(seconds, duration, width());

  const draw = () => {
    const w = width();
    const palette = readPalette(root);
    const active = turnAtTime(turns, position);
    context.clearRect(0, 0, w, height);

    // Tutor bands would sit here. The worker sends no tutor timing today, so
    // payload.tutor is always empty and nothing is invented to fill it.
    (payload.tutor || []).forEach(band => {
      context.fillStyle = palette.tutor;
      context.fillRect(x(band.start), WAVE_TOP, Math.max(1, x(band.end) - x(band.start)), WAVE_HEIGHT);
    });

    turns.forEach(turn => {
      const isActive = active && active.index === turn.index;
      context.fillStyle = isActive ? palette.turnActive : palette.turn;
      context.fillRect(x(turn.start), WAVE_TOP - 4, Math.max(2, x(turn.end) - x(turn.start)), WAVE_HEIGHT + 8);
      context.fillStyle = isActive ? palette.turnInk : palette.muted;
      context.font = `${isActive ? 800 : 600} 11px system-ui, -apple-system, sans-serif`;
      context.fillText(String(turn.index), x(turn.start) + 3, WAVE_TOP - 8);
    });

    if (peaks) {
      // Bucketing the cache down to the pixel width is the only expensive
      // thing in a redraw, and the width only changes on a resize or a zoom —
      // not sixty times a second while the recording plays.
      const wanted = Math.max(1, Math.round(w));
      if (!columnPeaks || columnPeaks.length !== wanted) columnPeaks = downsamplePeaks(peaks, wanted);
      const columns = columnPeaks;
      const middle = WAVE_TOP + WAVE_HEIGHT / 2;
      const playedTo = x(position);
      for (let i = 0; i < columns.length; i += 1) {
        const barHeight = Math.max(1, columns[i] * (WAVE_HEIGHT / 2));
        context.fillStyle = i <= playedTo ? palette.wavePlayed : palette.wave;
        context.fillRect(i, middle - barHeight, 1, barHeight * 2);
      }
    }

    (payload.flags || []).forEach(flag => {
      const at = x(flag.at);
      context.fillStyle = palette.flag;
      context.fillRect(at - 1, WAVE_TOP - 4, 2, WAVE_HEIGHT + 8);
      context.beginPath();
      context.moveTo(at - 6, WAVE_TOP - 4);
      context.lineTo(at + 6, WAVE_TOP - 4);
      context.lineTo(at, WAVE_TOP + 4);
      context.fill();
    });

    const laneTop = WAVE_TOP + WAVE_HEIGHT + LANE_GAP_TOP;
    clusteredLanes.forEach((lane, laneIndex) => {
      const y = laneTop + laneIndex * (LANE_HEIGHT + LANE_GAP);
      // The engine's name sits in the gap above its bar, so it never covers a
      // segment, and it is pinned to the left of what is on screen rather than
      // to second zero: zoomed in, a name anchored at the start of the
      // recording scrolls away and the lanes become anonymous coloured bars.
      context.fillStyle = palette.muted;
      context.font = '600 10px system-ui, -apple-system, sans-serif';
      context.fillText(lane.label, scroller.scrollLeft + 1, y - 4);

      context.fillStyle = palette.lane;
      context.fillRect(0, y, w, LANE_HEIGHT);
      lane.blocks.forEach(block => {
        context.fillStyle = block.silent ? palette.laneSilent : palette.laneOn;
        context.fillRect(x(block.start), y + 2, Math.max(2, x(block.end) - x(block.start)), LANE_HEIGHT - 4);
      });
    });

    const head = x(position);
    context.fillStyle = palette.playhead;
    context.fillRect(head - 1, 8, 2, height - 8);
    context.beginPath();
    context.arc(head, 8, 4, 0, Math.PI * 2);
    context.fill();

    if (timeLabel) timeLabel.textContent = `${formatClock(position)} / ${formatClock(duration)}`;
    if (chip) {
      chip.textContent = active ? `turn ${active.index} of ${turns.length}` : 'between turns';
      chip.classList.toggle('stt-wave__chip--on', Boolean(active));
    }
  };

  const layout = () => {
    const visible = scroller.clientWidth || 1;
    const w = Math.max(visible, Math.round(visible * zoom));
    stage.style.width = `${w}px`;
    stage.style.height = `${height}px`;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.round(w * ratio);
    canvas.height = Math.round(height * ratio);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${height}px`;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    draw();
  };

  const seek = seconds => {
    // A turn's play button in the table below parks a stop time on the player
    // and pauses there. Scrubbing means the reviewer wants somewhere else, so
    // that stop time has to go — otherwise the recording stopped dead a second
    // after every seek, at the end of a turn nobody was listening to.
    if (typeof audio.sttClearTurnStop === 'function') audio.sttClearTurnStop();
    position = Math.min(duration, Math.max(0, seconds));
    if (Number.isFinite(audio.duration)) audio.currentTime = Math.min(position, audio.duration);
    else audio.currentTime = position;
    draw();
  };

  const follow = () => {
    if (audio.paused) {
      frame = null;
      return;
    }
    position = audio.currentTime;
    draw();
    frame = window.requestAnimationFrame(follow);
  };

  const setPlayLabel = () => {
    if (playButton) playButton.textContent = audio.paused ? '▶ play' : '⏸ pause';
  };

  // --- pointer ------------------------------------------------------------
  const secondsAt = event => {
    const box = stage.getBoundingClientRect();
    return xToSeconds(event.clientX - box.left, duration, box.width);
  };

  stage.addEventListener('pointerdown', event => {
    const box = stage.getBoundingClientRect();
    const seconds = secondsAt(event);
    // The top ribbon is the turn strip: a click on a numbered band there
    // jumps to that turn's start, the way the table's play button does.
    // Below it the strip is a scrubber and a click means "this exact moment".
    if (event.clientY - box.top < WAVE_TOP) {
      const turn = turnAtTime(turns, seconds);
      if (turn) {
        seek(turn.start);
        return;
      }
    }
    dragging = true;
    if (stage.setPointerCapture) stage.setPointerCapture(event.pointerId);
    seek(seconds);
  });

  stage.addEventListener('pointermove', event => {
    const box = stage.getBoundingClientRect();
    const offset = event.clientX - box.left;
    const seconds = xToSeconds(offset, duration, box.width);
    const turn = turnAtTime(turns, seconds);
    if (cursor) {
      cursor.style.display = 'block';
      cursor.style.left = `${offset}px`;
    }
    if (tip) {
      tip.style.display = 'block';
      tip.style.left = `${offset}px`;
      tip.textContent = `${formatClock(seconds)}${turn ? ` · turn ${turn.index}` : ''}`;
    }
    if (dragging) seek(seconds);
  });

  const hideHover = () => {
    if (cursor) cursor.style.display = 'none';
    if (tip) tip.style.display = 'none';
  };
  stage.addEventListener('pointerleave', hideHover);
  stage.addEventListener('pointerup', () => { dragging = false; });
  stage.addEventListener('pointercancel', () => { dragging = false; hideHover(); });

  // --- keyboard -----------------------------------------------------------
  root.addEventListener('keydown', event => {
    if (shouldIgnoreKey(event.target)) return;
    const step = keyboardStep(event.key, { position, duration, turns });
    if (!step) return;
    event.preventDefault();
    if (step.toggle) {
      if (audio.paused) audio.play().catch(() => {}); else audio.pause();
      return;
    }
    seek(step.position);
  });

  if (playButton) {
    playButton.addEventListener('click', () => {
      if (audio.paused) audio.play().catch(() => {}); else audio.pause();
    });
  }

  // --- zoom ---------------------------------------------------------------
  scroller.addEventListener('wheel', event => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    const next = zoom * (event.deltaY < 0 ? 1.2 : 1 / 1.2);
    zoom = Math.min(MAX_ZOOM, Math.max(1, next));
    if (zoomLabel) zoomLabel.textContent = zoom <= 1.001 ? 'fit' : `${zoom.toFixed(1)}×`;
    layout();
    scroller.scrollLeft = x(position) - scroller.clientWidth / 2;
  }, { passive: false });

  // --- the audio element is the single source of truth for the playhead ----
  audio.addEventListener('timeupdate', () => {
    position = audio.currentTime;
    if (!frame) draw();
  });
  audio.addEventListener('play', () => {
    setPlayLabel();
    if (!frame) frame = window.requestAnimationFrame(follow);
  });
  audio.addEventListener('pause', setPlayLabel);
  audio.addEventListener('ended', setPlayLabel);
  audio.addEventListener('loadedmetadata', () => {
    // The decoded length is authoritative; this is the earlier of the two.
    if (Number.isFinite(audio.duration) && audio.duration > duration) {
      duration = audio.duration;
      layout();
    }
  });

  // Zoomed in, the pinned engine names have to be redrawn where the strip now
  // starts.
  scroller.addEventListener('scroll', () => {
    if (!frame) draw();
  });

  // The only listener not attached to a node inside this card, so it is the
  // only one Turbolinks cannot throw away with the page. Left on, every visit
  // added another strip's worth of redraws to every resize.
  const onResize = () => { if (!root.hidden) layout(); };
  window.addEventListener('resize', onResize);
  document.addEventListener('turbolinks:before-cache', () => {
    window.removeEventListener('resize', onResize);
  }, { once: true });

  // --- decode -------------------------------------------------------------
  root.hidden = false;
  if (status) {
    status.hidden = false;
    status.textContent = 'Reading the recording…';
  }
  setPlayLabel();
  layout();

  // The same-origin stream of the recording: the audio element may be
  // redirected to object storage, which fetch cannot read without CORS.
  decodePeaks(root.dataset.waveformSrc || audio.currentSrc || audio.src)
    .then(decoded => {
      if (!decoded) throw new Error('This browser cannot decode the recording.');
      peaks = decoded.peaks;
      columnPeaks = null;
      if (decoded.duration > 0) duration = Math.max(duration, decoded.duration);
      if (status) status.hidden = true;
      // One player per card: the strip's header is the transport now, so
      // the native controls go. The element stays for playback and seeking.
      audio.hidden = true;
      root.closest('.stt-call-audio')?.classList.add('stt-call-audio--wave');
      layout();
    })
    .catch(() => {
      // Nothing here is load-bearing: the by-turn table and its play buttons
      // are the page. A strip that cannot draw the audio removes itself.
      root.hidden = true;
    });
};

const initializePage = () => {
  document.querySelectorAll('[data-stt-waveform]').forEach(initialize);
};
document.addEventListener('DOMContentLoaded', initializePage);
document.addEventListener('turbolinks:load', initializePage);

// The pure helpers above are exercised by
// test/javascript/call_playground_waveform_test.js with plain Node and no DOM.
// The page itself requires this file only for its side effects
// (app/javascript/account/index.js), so the export is inert there.
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    downsamplePeaks,
    secondsToX,
    xToSeconds,
    turnAtTime,
    laneClusters,
    keyboardStep,
    shouldIgnoreKey,
    formatClock,
    stageHeight,
    bindOnce,
    ARROW_STEP_SECONDS,
    LANE_MERGE_GAP_SECONDS,
    TURN_TOLERANCE_SECONDS,
    MAX_ZOOM,
  };
}
